import { Action, Core, Message } from "@faire/slate/foundation/color";

import { buttonTheme } from "../../slate-deprecated/Button/theme";
import { DEPRECATED_STOP_USING_THIS_embeddedLinkTheme } from "../../slate-deprecated/EmbeddedLink/theme";
import { Weight } from "../Font";
import { fontRamp } from "../Typography/Themes";

import { Media2Strings } from "./Media2Strings";
import { ITheme } from "./Theme";

// Legacy Slate core token structure but using new Slate color token values, needed here to support legacy styled-components theming
const LegacySlateCoreColors = {
  background: {
    primary: Core.surface.primary,
    secondary: Core.surface.secondary,
    tertiary: Core.surface.secondary,
  },
  surface: {
    default: Core.surface.primaryInverse,
    subdued: Core.surface.secondary,
    muted: Core.surface.secondary,
    inverse: Core.surface.primary,
    hover: Action.surface.hover,
    enabled: Action.surface.active,
    disabled: Action.surface.disabled,
    success: Message.surface.success,
    warning: Message.surface.warning,
    critical: Message.surface.critical,
    info: Message.surface.info,
  },
  text: {
    default: Core.text.primary,
    subdued: Core.text.subdued,
    inverse: Core.text.primaryInverse,
    hover: Action.text.hover,
    enabled: Action.text.active,
    disabled: Action.text.disabled,
    critical: Message.text.critical,
    primary: Core.text.primary,
    primaryInverse: Core.text.primaryInverse,
    secondary: Core.text.subdued,
  },
  border: {
    default: Action.border.default,
    subdued: Core.border.subdued,
    muted: Core.border.muted,
    inverse: Action.border.inverse,
    hover: Action.border.hover,
    enabled: Action.border.active,
    disabled: Action.border.disabled,
    success: Message.border.success,
    warning: Message.border.warning,
    critical: Message.border.critical,
    info: Message.border.info,
    primary: Core.border.muted,
  },
  icon: {
    default: Core.icon.primary,
    subdued: Core.icon.subdued,
    inverse: Core.icon.primaryInverse,
    hover: Action.icon.hover,
    enabled: Action.icon.active,
    disabled: Action.icon.disabled,
    success: Message.icon.success,
    warning: Message.icon.warning,
    critical: Message.icon.critical,
    info: Message.icon.info,
    favorite: Action.surface.favorited,
    primary: Core.icon.primary,
    secondary: Core.icon.subdued,
  },
  opacity: {
    overlay: Core.surface.overlay,
    mask: Core.surface.mask,
  },
};

/**
 * Default theme used for Faire properties.
 */
export const DefaultTheme: ITheme = {
  name: "Faire",
  ...LegacySlateCoreColors,
  toggle: {
    active: {
      on: {
        label: {
          text: Core.text.primary,
        },
        toggle: {
          container: {
            surface: Core.surface.primaryInverse,
            border: Action.border.default,
          },
          indicator: {
            surface: Core.surface.primary,
          },
        },
      },
      off: {
        label: {
          text: Core.text.primary,
        },
        toggle: {
          container: {
            surface: Core.surface.primary,
            border: Core.border.subdued,
          },
          indicator: {
            surface: Action.icon.disabled,
          },
        },
      },
    },
    disabled: {
      on: {
        label: {
          text: Action.text.disabled,
        },
        toggle: {
          container: {
            surface: Action.surface.disabled,
            border: "transparent",
          },
          indicator: {
            surface: Action.icon.disabled,
          },
        },
      },
      off: {
        label: {
          text: Action.text.disabled,
        },
        toggle: {
          container: {
            surface: Core.surface.secondary,
            border: "transparent",
          },
          indicator: {
            surface: Action.icon.disabled,
          },
        },
      },
    },
  },
  input: {
    label: { color: Core.text.primary },
    placeholder: { color: Core.text.subdued },
    helper: { variant: "labelSansRegular", color: Core.text.primary },
  },
  checkboxes: {
    primary: {
      icon: Core.icon.primaryInverse,
      fill: Core.surface.primaryInverse,
      border: Action.border.default,
      borderActive: Action.border.active,
      base: { color: Core.text.primary },
      checked: { color: Core.text.primary },
      hover: { color: Action.text.hover },
    },
    secondary: {
      icon: Core.icon.primary,
      fill: Core.surface.primary,
      border: Action.border.default,
      borderActive: Action.border.default,
      base: { color: Core.text.primary },
      checked: { color: Core.text.primary },
      hover: { color: Action.text.hover },
    },
    disabled: {
      icon: Action.icon.disabled,
      fill: Action.surface.disabled,
      border: Core.border.muted,
      borderActive: Core.border.muted,
      base: { color: Action.text.disabled },
      checked: { color: Action.text.disabled },
      hover: { color: Action.text.disabled },
    },
  },
  radios: {
    enabled: Core.text.primary,
    disabled: Action.text.disabled,
  },
  pills: {
    filter: {
      primary: {
        color: Core.text.primary,
        hover: Action.text.hover,
        contrast: Core.surface.primary,
      },
      secondary: {
        color: Core.text.primary,
        hover: Core.border.muted,
        contrast: Action.text.hover,
      },
      label: {},
    },
    tag: {
      background: {
        color: "var(--slate-color-surface-tertiary)",
        hover: Action.surface.inverseHover,
        contrast: Core.text.subdued,
      },
      label: {},
    },
  },
  modals: {
    primary: {
      background: Action.surface.active,
      color: Core.text.primaryInverse,
      iconProps: { strokeWidth: 2 },
      // PREVIOUS TOKEN: Action.surface.active + 99
      overlay: "#000000" + "99",
      boxShadow: Core.surface.primaryInverse,
    },
    default: {
      background: Core.surface.primary,
      color: Core.text.primary,
      iconProps: { strokeWidth: 2 },
      // PREVIOUS TOKEN: Action.surface.active + 99
      overlay: "#000000" + "99",
      boxShadow: Core.surface.primaryInverse,
    },
  },
  buttons: {
    primary: {
      background: {
        color: Core.surface.primaryInverse,
        hover: Action.surface.hover,
      },
      text: {
        color: Core.text.primaryInverse,
        hover: Core.text.primaryInverse,
      },
      border: {
        color: Action.border.default,
        hover: Action.border.hover,
      },
      disabled: {
        background: Action.surface.disabled,
        border: Action.surface.disabled,
        text: Action.text.disabled,
      },
    },
    secondary: {
      background: {
        color: Core.surface.primary,
        hover: Action.surface.hover,
      },
      text: {
        color: Core.text.primary,
        hover: Core.text.primaryInverse,
      },
      border: {
        color: Action.border.default,
        hover: Action.border.hover,
      },
      disabled: {
        background: Core.surface.primary,
        border: Action.border.disabled,
        text: Action.text.disabled,
      },
    },
  },
  embeddedLink: DEPRECATED_STOP_USING_THIS_embeddedLinkTheme,
  tooltips: {
    primary: {
      background: Core.surface.primaryInverse,
      color: Core.text.primaryInverse,
      border: Action.border.default,
      actionButtons: {
        disabled: {
          background: Action.surface.disabled,
          color: Action.icon.active,
        },
        enabled: {
          background: Core.surface.primary,
          color: Action.icon.active,
        },
      },
    },
    default: {
      background: Core.surface.primary,
      color: Core.text.primary,
      border: Core.border.muted,
      actionButtons: {
        disabled: {
          background: Core.surface.primary,
          color: Action.text.disabled,
        },
        enabled: {
          background: Core.surface.primary,
          color: Core.text.primary,
        },
      },
    },
  },
  stepper: {
    step: {
      active: {
        text: Core.text.primaryInverse,
        surface: Core.surface.primaryInverse,
        border: Action.border.default,
      },
      inactive: {
        text: Core.text.subdued,
        surface: Core.surface.primary,
        border: Core.border.subdued,
      },
      completed: {
        surface: Action.surface.disabled,
        border: Core.border.subdued,
        text: Action.text.disabled,
      },
    },
    label: {
      active: {
        text: Core.text.primary,
      },
      inactive: {
        text: Core.text.subdued,
      },
      completed: {
        text: Core.text.subdued,
      },
    },
    line: {
      complete: {
        surface: Core.surface.primaryInverse,
      },
      incomplete: {
        surface: Core.surface.secondary,
      },
    },
  },
  avatar: {
    placeholder: {
      background: Core.surface.primaryInverse,
      color: Core.text.primaryInverse,
    },
    border: {
      color: Core.border.muted,
    },
    background: Core.surface.primary,
  },
  // TODO(alipianu): delete this (it's not really used anywhere, its unuse is obfuscated by convoluted getFontStyles() / convertPropsToStyle() logic)
  fonts: {
    serif: {
      // Display L
      h1: {
        family: () => "nantes",
        weight: Weight.medium,
        size: "52px",
        lineHeight: "64px",
        letterSpacing: "0px",
      },
      // Display M
      h2: {
        family: () => "nantes",
        weight: Weight.medium,
        size: "38px",
        lineHeight: "50px",
        letterSpacing: "0px",
      },
      // Display S
      h3: {
        family: () => "nantes",
        weight: Weight.medium,
        size: "30px",
        lineHeight: "38px",
        letterSpacing: "0px",
      },
      // Page Header
      h4: {
        family: () => "nantes",
        weight: Weight.medium,
        size: "22px",
        lineHeight: "32px",
        letterSpacing: "0px",
      },
      h5: {
        family: () => "nantes",
        weight: Weight.medium,
        size: "18px",
        lineHeight: "26px",
        letterSpacing: "0px",
      },
      p: {
        family: () => "nantes",
        weight: Weight.medium,
        size: "14px",
        lineHeight: "20px",
        letterSpacing: "0px",
      },
      small: {
        family: () => "nantes",
        weight: Weight.medium,
        size: "12px",
        lineHeight: "16px",
        letterSpacing: "0px",
      },
    },
    sansSerif: {
      h1: {
        family: () => "graphik",
        weight: Weight.bold,
        size: "52px",
        lineHeight: "64px",
        letterSpacing: "0.15px",
      },
      h2: {
        family: () => "graphik",
        weight: Weight.bold,
        size: "38px",
        lineHeight: "50px",
        letterSpacing: "0.15px",
      },
      // Display S (can also be 200 - "Regular")
      h3: {
        family: () => "graphik",
        weight: Weight.bold,
        size: "30px",
        lineHeight: "38px",
        letterSpacing: "0.15px",
      },
      // Section Header
      h4: {
        family: () => "graphik",
        weight: Weight.medium,
        size: "22px",
        lineHeight: "28px",
        letterSpacing: "0.15px",
      },
      // Subheading (can also be 200 - "Regular")
      h5: {
        family: () => "graphik",
        weight: Weight.medium,
        size: "18px",
        lineHeight: "26px",
        letterSpacing: "0.15px",
      },
      // Paragraph
      p: {
        family: () => "graphik",
        weight: Weight.book,
        size: "14px",
        lineHeight: "20px",
        letterSpacing: "0.15px",
      },
      // Label
      small: {
        family: () => "graphik",
        weight: Weight.book,
        size: "12px",
        lineHeight: "16px",
        letterSpacing: "0.15px",
      },
    },
  },
  banners: {},
  pagination: {
    activePage: {
      color: Core.text.primary,
      hover: Action.text.hover,
      contrast: Core.text.primaryInverse,
    },
    inactivePage: {
      color: "transparent",
      hover: "transparent",
      contrast: Core.text.primary,
    },
    text: {
      variant: "labelSansRegular",
      align: "center",
    },
  },
  typography: fontRamp,
  buttons2: buttonTheme,
  media2strings: new Media2Strings(),
  spacing: 8,
} as const;
